<div class="relative p-4">
  <ng-container>
    <!-- Header -->
    <div class="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
      <h2 class="text-gray-400 poppins-medium text-2xl md:text-3xl !p-0">GST Reconciliation</h2>
      <div class="flex justify-between items-center gap-4">
        <p-button
          [rounded]="true"
          label="Help & Getting Started"
          class="poppins-semibold text-sm md:text-base"
          icon="pi pi-info-circle"
          (onClick)="showHelp()"
          outlined="true"
          size="small"
        />
      </div>
    </div>

    <!-- Quick Guide Section -->
    <div class="mb-6">
      <p-fieldset
        legend="Quick Guide to ReconEase"
        class="poppins-medium shadow-md hover:bg-slate-100 hover:cursor-pointer"
      >
        <div class="flex flex-col md:flex-row gap-4">
          <div class="w-full md:w-1/2">
            <p>1. Bring Credit Register & GSTR 2B into a single file.</p>
            <p>2. Name the sheets: <strong>"<span class="code" (click)="copyToClipboard('Books')">Books</span>"</strong> for Credit Register & <strong>"<span class="code" (click)="copyToClipboard('GSTR - 2B')">GSTR - 2B</span>"</strong> for GSTR 2B data.</p>
            <p>3. Ensure the first row in each sheet contains headers.</p>
            <p>4. Rename selected headers in Books sheet:</p>
            <ul>
              <li>
                <span class="code" (click)="copyToClipboard('GSTN')">GSTN</span>,
                <span class="code" (click)="copyToClipboard('INVOICE_NUMBER')">INVOICE_NUMBER</span>,
                <span class="code" (click)="copyToClipboard('TAXABLE_VALUE')">TAXABLE_VALUE</span>,
                <span class="code" (click)="copyToClipboard('IGST')">IGST</span>,
                <span class="code" (click)="copyToClipboard('CGST')">CGST</span>,
                <span class="code" (click)="copyToClipboard('SGST')">SGST</span>,
                <span class="code" (click)="copyToClipboard('INVOICE_DATE')">INVOICE_DATE</span>
              </li>
            </ul>
            <p>5. Rename selected headers in GSTR-2B sheet:</p>
            <ul>
              <li>
                <span class="code" (click)="copyToClipboard('GSTN')">GSTN</span>,
                <span class="code" (click)="copyToClipboard('INVOICE_NUMBER')">INVOICE_NUMBER</span>,
                <span class="code" (click)="copyToClipboard('TAXABLE_VALUE')">TAXABLE_VALUE</span>,
                <span class="code" (click)="copyToClipboard('IGST')">IGST</span>,
                <span class="code" (click)="copyToClipboard('CGST')">CGST</span>,
                <span class="code" (click)="copyToClipboard('SGST')">SGST</span>,
                <span class="code" (click)="copyToClipboard('ITC_AVAILABILITY')">ITC_AVAILABILITY</span>,
                <span class="code" (click)="copyToClipboard('INVOICE_DATE')">INVOICE_DATE</span>
              </li>
            </ul>
            <p>📌 Upload your file. Enter tolerance & month—sit back and watch the magic happen! ✨</p>
            <p>🔥 <strong>Pro Tip:</strong> Our system is case-sensitive and space-sensitive. For best results, copy & paste sheet names and headers exactly as mentioned! ✅</p>
            <p>📌 <strong>Note: </strong>To Copy the context please click on the highlighted text.</p>
          </div>
          <div class="w-full md:w-1/2">
            <div class="cursor-pointer">
              <video controls [poster]="thumbnailSource" class="h-full w-full rounded-lg" height="240"
              #videoPlayer>
              <source src="{{videoSource}}" type="video/mp4">
              Your browser does not support the video tag.
            </video>
              <p class="poppins-medium text-center mt-2">Click the video for Quick Demo</p>
            </div>
          </div>
        </div>
      </p-fieldset>
    </div>

    <!-- Upload Container -->
    <div class="flex w-full mb-6">
      <app-file-upload class="w-full" (selectedFiles)="fileUpload($event)" (reset)="onReset()" [requestStatus]="requestStatus"></app-file-upload>
    </div>

    <!-- Response Section -->
    <div>
      <div *ngIf="requestStatus === REQUEST_STATUS.Completed">
        <section #responseContainer>
          <app-gstr-response [input]="fileData" [downloadFile]="true"></app-gstr-response>
        </section>
      </div>

      <div class="fixed bottom-4 right-2" *ngIf="requestStatus === REQUEST_STATUS.Completed">
        <button
          class="bg-primary poppins-semibold text-white px-4 py-2 border-0 outline-none rounded-2xl cursor-pointer"
          (click)="onReset()"
        >
          Match new file
        </button>
      </div>
    </div>
  </ng-container>

  <!-- Dialog for Help -->
  <div class="card flex justify-content-center w-full md:w-2/4 flex-wrap absolute">
    <p-confirmDialog
      icon="pi pi-exclamation-triangle"
      [acceptLabel]="'close'"
      [closable]="true"
      [acceptIcon]="''"
      [rejectVisible]="false"
    />
  </div>

  <!-- User Guide Dialog -->
  <p-dialog
    header="User Guide for Credit Register & GSTR-2B Reconciliation Tool"
    [(visible)]="visible"
    [modal]="true"
    [breakpoints]="{ '1199px': '75vw', '575px': '90vw' }"
    [style]="{ width: '50vw' }"
    [draggable]="false"
    [resizable]="false"
  >
  <p-scrollPanel class="poppins-regular" [style]="{ width: '100%', height: '300px' }">
    <h3>Introduction</h3>
    <p>This tool is designed to assist users in reconciling data between the Credit Register (Books) and GSTR-2B from
      the GST portal. Users need to upload an Excel workbook containing two sheets: <span class="poppins-semibold">Books</span> and <span class="poppins-semibold">GSTR-2B.</span> </p>
    <p>To ensure flexibility, there is no fixed format for the files. Users can export the Credit Register from their
      respective accounting software and rename specific columns as follows:</p>
    <hr>

    <h3>Required Columns for Each Sheet</h3>

    <p-table [value]="helpTableData" styleClass="p-datatable-striped p-datatable-gridlines" class="w-full">
      <ng-template pTemplate="header">
        <tr>
          <th class="bg-gray-200 text-left p-3">Required Fields</th>
          <th class="bg-gray-200 text-center p-3">Books</th>
          <th class="bg-gray-200 text-center p-3">GSTR-2B</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-rowData>
        <tr>
          <td class="p-3 border">{{ rowData.field }}</td>
          <td class="p-3 border text-center">
            <i [ngClass]="rowData.books ? 'pi pi-check-circle text-green-500' : 'pi pi-times-circle text-red-500'"></i>
          </td>
          <td class="p-3 border text-center">
            <i [ngClass]="rowData.gstr2b ? 'pi pi-check-circle text-green-500' : 'pi pi-times-circle text-red-500'"></i>
          </td>
        </tr>
      </ng-template>
    </p-table>
    <hr>
    <h3>How the Tool Works</h3>
    <ol>
      <li>Upload the Excel file containing the two required sheets (Books and GSTR-2B).</li>
      <li><span class="poppins-semibold">Automatic Matching:</span> The system processes and matches records based on predefined rules.</li>
      <li><span class="poppins-semibold">Output Workbooks:</span> The tool generates two processed workbooks:
        <ul>
          <li><span class="poppins-semibold">Processed Books:</span> Check the ‘Matched’ column to verify matches.</li>
          <li><span class="poppins-semibold">Processed GSTR-2B:</span> Check the ‘Matched’ column for matching status.</li>
        </ul>
      </li>
      <li><span class="poppins-semibold">Match ID:</span> Each matched record is assigned a <span class="poppins-semibold">Match ID</span> to help users trace linked entries across both sheets.
      </li>
      <li><span class="poppins-semibold">Rule-Based Matching:</span> The tool applies multiple matching rules to reconcile data effectively.</li>
    </ol>
    <hr>

    <h3>Understanding the Results</h3>
    <p>The system employs different reconciliation rules based on invoice numbers, tax amounts, and GSTN with tolerance levels and categorises the matches into Perfect Match, Near Match and Probable Match:</p>
    <h4>1.	Perfect Match without tolerance</h4>
    <p>Refers to matches where invoice numbers are perfectly matching without any difference in Tax values</p>
    <p>Many to one matching is also done where in multiple line items in Books is matched with a single entry in GSTR-2B. Similarly single line items in Books is matched with multiple line items in GSTR-2B based on invoice number.</p>

    <h4>2. Perfect Match with tolerance</h4>
    <p>Refers to matches where invoice numbers are perfectly matching but tax values differ without any difference in Tax values.</p>

    <h4>3. Near Match</h4>
    <p>Matching is done where invoices number in books nearly matches with invoice number in GSTR 2B. Many to one and One to many matching is also done this way.</p>

    <h4>4.	Near Match with tolerance</h4>
    <p>Invoice number in books nearly matches with invoice number 2B and tax values differ to the extent of Tolerance set by user.</p>

    <h4>5.	Probable Match by Taxable Value, Tax & GSTN</h4>
    <p>System is unsure if the matches are actually a match but gives user a probable match. User may accept these matches only after careful consideration.</p>
    <p><span class="poppins-medium text-[#ffcc00b5]"> <i class="pi pi-exclamation-triangle"></i>
        Caution: </span>Users should verify such matches carefully before and observe extreme caution  while accepting these matches.</p>
    <hr>

    <h3>Disclaimer</h3>
    <p>This reconciliation tool is designed to assist users in matching Credit Register (Books) with GSTR-2B. While every effort has been made to ensure accuracy, <span class="poppins-medium">users are responsible for verifying the correctness of matched data.</span> The tool does not guarantee compliance with GST laws, and users should review all results before making financial or tax-related decisions.</p>
    <p>By using this tool, users acknowledge that they assume full responsibility for any discrepancies or compliance issues arising from the reconciliation process.</p>
  </p-scrollPanel>
    <div class="flex items-center justify-end gap-2 mt-4">
      <p-button label="Close" (onClick)="showHelp()" [outlined]="true" size="small" severity="danger" (onClick)="visible = false" />
    </div>
  </p-dialog>
</div>
